// Single source of truth for all plan definitions
// Stripe IDs (live): Professional = prod_UOQjZpuMj2ZAya, Team = prod_UOQmM2PK10UrYW
// Prices v2 (2026-05): Pro $399/mo · $3,990/yr · Team $999/mo · $9,990/yr

export interface PlanDefinition {
  id: string;
  name: string;
  price: number | null;
  yearlyPrice: number | null;
  seats: number | 'Unlimited';
  proposalLimit: number | null; // null = unlimited
  description: string;
  features: string[];
  popular?: boolean;
  stripePriceId: string | null; // monthly (legacy alias)
  stripePriceIdMonthly: string | null;
  stripePriceIdYearly: string | null;
  stripeProductId: string | null;
}

export const PLANS: PlanDefinition[] = [
  {
    id: 'professional',
    name: 'Professional',
    price: 399,
    yearlyPrice: 333,
    seats: 1,
    proposalLimit: 10,
    description: 'For bid writers who need to move faster and win more.',
    features: [
      '1 user',
      '10 proposals per month',
      'Go/No-Go bid assessment',
      'AI proposal drafting',
      'Knowledge Vault with smart import',
      'Factual accuracy checks',
      'Proposal outline + drag-and-drop',
      'Word export',
      'Bid performance analytics',
      'Email support',
    ],
    stripePriceId: 'price_1TTbXOR1g6Gb7ZhUJrziou9J',
    stripePriceIdMonthly: 'price_1TTbXOR1g6Gb7ZhUJrziou9J',
    stripePriceIdYearly: 'price_1TTbXuR1g6Gb7ZhUe6Ef91gY',
    stripeProductId: 'prod_UOQjZpuMj2ZAya',
  },
  {
    id: 'team',
    name: 'Team',
    price: 999,
    yearlyPrice: 833,
    seats: 5,
    proposalLimit: null,
    description: 'For bid teams who need to collaborate and scale.',
    popular: true,
    features: [
      'Everything in Professional, plus:',
      '5 seats included ($99/seat after)',
      'Unlimited proposals',
      'Role-based permissions — Owner, Admin, Manager, Contributor, Viewer',
      'Priority support (24hr response)',
    ],
    stripePriceId: 'price_1TTbYFR1g6Gb7ZhUtz6IDivW',
    stripePriceIdMonthly: 'price_1TTbYFR1g6Gb7ZhUtz6IDivW',
    stripePriceIdYearly: 'price_1TTbYYR1g6Gb7ZhUlXBWM6QL',
    stripeProductId: 'prod_UOQmM2PK10UrYW',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: null,
    yearlyPrice: null,
    seats: 'Unlimited',
    proposalLimit: null,
    description: 'For organisations running high-volume, high-value tenders',
    features: [
      'Everything in Team, plus:',
      'Unlimited seats',
      'Unlimited proposals',
      'Dedicated success manager',
      'Dedicated onboarding',
      'SLA guarantee',
      'Custom contract & billing',
    ],
    stripePriceId: null,
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,
    stripeProductId: null,
  },
];

/** Plan hierarchy for upgrade/downgrade logic */
export const PLAN_HIERARCHY = ['professional', 'team', 'enterprise'] as const;

/** Stripe product ID → plan info mapping (for edge functions) */
export const PRODUCT_TO_PLAN: Record<string, { planType: string; seatLimit: number }> = {
  'prod_UOQjZpuMj2ZAya': { planType: 'professional', seatLimit: 1 },
  'prod_UOQmM2PK10UrYW': { planType: 'team', seatLimit: 5 },
};

/** Stripe price ID mapping (for edge functions) — defaults to monthly */
export const PRICE_IDS: Record<string, string> = {
  professional: 'price_1TTbXOR1g6Gb7ZhUJrziou9J',
  team: 'price_1TTbYFR1g6Gb7ZhUtz6IDivW',
};

/** Stripe price ID mapping by billing cycle */
export const PRICE_IDS_BY_CYCLE = {
  monthly: {
    professional: 'price_1TTbXOR1g6Gb7ZhUJrziou9J',
    team: 'price_1TTbYFR1g6Gb7ZhUtz6IDivW',
  },
  yearly: {
    professional: 'price_1TTbXuR1g6Gb7ZhUe6Ef91gY',
    team: 'price_1TTbYYR1g6Gb7ZhUlXBWM6QL',
  },
} as const;

/** Team plan additional-seat add-on (per extra seat above the 5 included) */
export const TEAM_SEAT_ADDON = {
  productId: 'prod_USWzAEzHlcexLB',
  monthly: { priceId: 'price_1TTbv3R1g6Gb7ZhUA3B1LVug', amount: 99 },
  yearly: { priceId: 'price_1TTbvTR1g6Gb7ZhU1bm3MOAc', amount: 990 },
} as const;

export const TEAM_BASE_SEATS = 5;

/** Views that require Team plan or higher.
 * As of 2026-05, all analytics views are included on every paid plan.
 * Only the per-month proposal counter on Overview remains Pro-only. */
export const TEAM_ONLY_ANALYTICS_VIEWS = [] as const;

export function getPlanById(id: string): PlanDefinition | undefined {
  return PLANS.find(p => p.id === id);
}

export function normalizePlanId(id: string | null): string {
  if (!id) return 'professional';
  const lower = id.toLowerCase();
  if (lower === 'starter' || lower === 'pro') return 'professional';
  return lower;
}

export function isTeamOrHigher(planType: string | null): boolean {
  const normalized = normalizePlanId(planType);
  const idx = PLAN_HIERARCHY.indexOf(normalized as typeof PLAN_HIERARCHY[number]);
  const teamIdx = PLAN_HIERARCHY.indexOf('team');
  return idx >= teamIdx;
}
